import { useEffect, useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { useDebouncedCallback } from 'use-debounce';

/*
Example configDefinition

{
  599: {
    ...the config you would like for screen sizes below and including 599
  },
  762: {
    ...the config you would like for screen sizes below 762 and greater than 599
  },
  default: {
    ...the config to use for all other breakpoints and first load
  }
  ...etc
};
 */

export const useResponsiveConfig = ({ configDefinition }) => {
  const configs = Object.keys(configDefinition);

  const breakPoints = configs
    .reduce((acc, breakPoint) => {
      if (breakPoint !== 'default') {
        acc.push(parseInt(breakPoint, 10));
      }
      return acc;
    }, [])
    .sort();

  const maxBreakPoint = Math.max(...breakPoints);

  const [activeBreakPoint, setActiveBreakPoint] = useState('default');
  const [activeConfig, setActiveConfig] = useState(configDefinition.default || {});

  const applyDefault = useCallback(() => {
    if (activeBreakPoint !== 'default') {
      setActiveConfig(configDefinition.default);
      setActiveBreakPoint('default');
    }
  }, [activeBreakPoint, configDefinition.default]);

  const determineConfig = useDebouncedCallback(
    () => {
      const { innerWidth } = window;

      if (breakPoints.length === 0 || innerWidth > maxBreakPoint) {
        applyDefault();
        return;
      }

      let breakPointMatch;

      breakPoints.forEach((breakPoint) => {
        if (breakPoint > innerWidth) {
          if (!breakPointMatch || breakPoint < breakPointMatch) {
            breakPointMatch = breakPoint;
          }
        }
      });

      if (activeBreakPoint !== breakPointMatch) {
        setActiveConfig(configDefinition[breakPointMatch]);
        setActiveBreakPoint(breakPointMatch);
        return;
      }

      if (!breakPointMatch) {
        applyDefault();
      }
    },
    500,
    { maxWait: 2000 }
  );

  useEffect(() => {
    determineConfig();

    window.addEventListener('resize', determineConfig);
    return () => {
      window.removeEventListener('resize', determineConfig);
    };
  }, [determineConfig]);

  return activeConfig || configDefinition.default || {};
};

useResponsiveConfig.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  configDefinition: PropTypes.any
};
